import { Box } from '@byecode/ui'
import type { Workflow, WorkflowLog } from '@lighthouse/shared'
import { ReactFlowProvider } from '@xyflow/react'
import { clone } from 'rambda'
import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import styled from 'styled-components'

import type { FlowDetailShape, FlowModePayload } from '@/contexts/FlowContext'
import { FlowContainerProvider, useFlowContainerContext } from '@/contexts/FlowContext'

import FlowDetailContent from './FlowDetailContent'
import { FlowModalManager } from './FlowModalManager'

const SCxFlowDetailWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    background-color: #fff;
`

export interface FlowEditorRef {
    requestClose: () => void
}

interface FlowEditorProps {
    name: string | undefined
    data: Workflow
    shape: FlowDetailShape
    scale?: number
    log?: WorkflowLog
    onFlowChange?: (val: FlowModePayload) => void
    onBack?: () => void
    onClose?: () => void
}

const FlowEditorRefBridge = forwardRef<FlowEditorRef>((_, ref) => {
    const { requestClose } = useFlowContainerContext()

    useImperativeHandle(
        ref,
        () => ({
            requestClose
        }),
        [requestClose]
    )

    return null
})

FlowEditorRefBridge.displayName = 'FlowEditorRefBridge'

export const FlowEditor = forwardRef<FlowEditorRef, FlowEditorProps>(
    ({ name = '未命名', data, shape, scale, log, onFlowChange, onClose }, ref) => {
        const {
            id,
            type,
            runType,
            errorMap,
            content: { nodes, edges },
            enabled
        } = data
        const elRef = useRef<HTMLDivElement>(null)
        return (
            <Box style={{ height: '100%', width: '100%' }} ref={elRef}>
                <FlowContainerProvider
                    id={id}
                    scale={scale}
                    name={name}
                    type={type}
                    runType={runType}
                    shape={shape}
                    errorMap={errorMap}
                    log={log}
                    enabled={enabled}
                    elRef={elRef}
                    onClose={onClose}
                    onFlowChange={onFlowChange}
                >
                    <FlowEditorRefBridge ref={ref} />
                    <ReactFlowProvider defaultEdges={edges} defaultNodes={clone(nodes)}>
                        <SCxFlowDetailWrapper>
                            <FlowDetailContent />
                        </SCxFlowDetailWrapper>
                        <FlowModalManager />
                    </ReactFlowProvider>
                </FlowContainerProvider>
            </Box>
        )
    }
)

FlowEditor.displayName = 'FlowEditor'
